import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Video, PhoneOff, Phone } from "lucide-react";
import { api } from "../lib/api";
import { Button } from "./ui/button";
import Avatar from "./Avatar";

export default function IncomingCall() {
  const [call, setCall] = useState(null);
  const handled = useRef(new Set());
  const ctxRef = useRef(null);
  const ringId = useRef(null);
  const nav = useNavigate();
  const { t } = useTranslation();

  useEffect(() => {
    let stop = false;
    const poll = async () => {
      try {
        const { data } = await api.get("/planning/chat/incoming-call");
        const c = data.call;
        if (stop) return;
        if (c && !handled.current.has(c.room)) setCall(c);
        else if (!c) setCall(null);
      } catch {}
    };
    poll();
    const id = setInterval(poll, 4000);
    return () => { stop = true; clearInterval(id); };
  }, []);

  useEffect(() => {
    const stopRing = () => {
      if (ringId.current) clearInterval(ringId.current);
      ringId.current = null;
      try { ctxRef.current && ctxRef.current.close(); } catch {}
      ctxRef.current = null;
    };
    if (!call) { stopRing(); return; }
    try {
      const AC = window.AudioContext || window.webkitAudioContext;
      const ctx = new AC();
      ctxRef.current = ctx;
      const beep = () => {
        try {
          if (ctx.state === "suspended") ctx.resume();
          const o = ctx.createOscillator();
          const g = ctx.createGain();
          o.type = "sine"; o.frequency.value = 880;
          g.gain.setValueAtTime(0.0001, ctx.currentTime);
          g.gain.exponentialRampToValueAtTime(0.22, ctx.currentTime + 0.05);
          g.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.5);
          o.connect(g); g.connect(ctx.destination);
          o.start(); o.stop(ctx.currentTime + 0.55);
        } catch {}
      };
      beep();
      ringId.current = setInterval(beep, 1500);
    } catch {}
    return stopRing;
  }, [call]);

  const dismiss = async () => {
    const room = call?.room;
    if (room) { handled.current.add(room); try { await api.post(`/planning/chat/calls/${room}/dismiss`); } catch {} }
    setCall(null);
  };
  const join = async () => {
    const c = call;
    if (!c) return;
    handled.current.add(c.room);
    setCall(null);
    try { await api.post(`/planning/chat/calls/${c.room}/dismiss`); } catch {}
    nav(`/messagerie?c=${c.conversation_id}&call=${c.room}`);
  };

  if (!call) return null;
  return (
    <div className="fixed top-4 right-4 z-[120] w-[320px] max-w-[92vw] rounded-2xl border border-emerald-400/30 bg-[#0f2130] shadow-2xl shadow-emerald-900/40 p-4 animate-in slide-in-from-top-2" data-testid="incoming-call-banner">
      <div className="flex items-center gap-3">
        <div className="relative">
          <Avatar name={call.caller_name} size={44} />
          <span className="absolute -bottom-1 -right-1 h-5 w-5 rounded-full bg-emerald-500 flex items-center justify-center animate-pulse"><Video className="h-3 w-3 text-slate-900" /></span>
        </div>
        <div className="min-w-0">
          <div className="text-sm font-bold text-slate-100 truncate">{call.is_group && call.title ? call.title : call.caller_name}</div>
          <div className="text-xs text-emerald-300">{t("messagerie.incomingCall")}</div>
        </div>
      </div>
      <div className="flex gap-2 mt-3">
        <Button onClick={join} data-testid="incoming-call-join" className="flex-1 bg-emerald-500 hover:bg-emerald-400 text-slate-900 font-semibold h-9"><Phone className="h-4 w-4 mr-1" />{t("messagerie.acceptCall")}</Button>
        <Button onClick={dismiss} data-testid="incoming-call-decline" variant="outline" className="flex-1 border-red-400/40 text-red-300 hover:bg-red-500/10 h-9"><PhoneOff className="h-4 w-4 mr-1" />{t("messagerie.declineCall")}</Button>
      </div>
    </div>
  );
}
